import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import onsiteproLogo from "../../assets/logo2 2.png";

const THEME = "#8b1634";

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function formatDateLabel(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function toYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

// Monday of the week containing date d
function getMonday(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon=0 ... Sun=6
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

const headerCell = { fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const cellEllipsis = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const dataCell = { color: "#2d3748", fontSize: "14px", ...cellEllipsis };

const workerKeyOf = (r) => (r.workerId ? String(r.workerId) : `name:${r.workerName}`);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function BillingReportPage() {
  const [month, setMonth] = useState(""); // "YYYY-MM"
  const [amountPerHour, setAmountPerHour] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appliedRange, setAppliedRange] = useState({ from: "", to: "" });
  const [selected, setSelected] = useState(null); // { key, workerName }
  const [monthOpen, setMonthOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  const monthRef = useRef(null);
  const [periodType, setPeriodType] = useState("month"); // "month" | "week"
  const [weekStart, setWeekStart] = useState(null); // Monday Date
  const [checkedKeys, setCheckedKeys] = useState(() => new Set()); // record keys ticked to mark paid
  const [savingPaid, setSavingPaid] = useState(false);
  const [detailModal, setDetailModal] = useState(null); // { date, items } — visits of one shift/date
  const [confirmSave, setConfirmSave] = useState(false); // confirm dialog before marking paid

  useEffect(() => {
    const onDoc = (e) => {
      if (monthRef.current && !monthRef.current.contains(e.target)) setMonthOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggleChecked = (key) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Toggle every unpaid record of a grouped shift/date at once.
  const toggleGroup = (unpaidKeys) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      const allIn = unpaidKeys.length > 0 && unpaidKeys.every((k) => next.has(k));
      unpaidKeys.forEach((k) => { if (allIn) next.delete(k); else next.add(k); });
      return next;
    });
  };

  // Validate then open the confirm dialog (paid is permanent).
  const requestSave = () => {
    if (!validRate) {
      toast.error('Enter "Amount per hour" before saving.');
      return;
    }
    if (checkedKeys.size === 0) {
      toast.info("Select at least one record to mark as paid.");
      return;
    }
    setConfirmSave(true);
  };

  const handleSavePaid = async () => {
    setConfirmSave(false);
    if (!validRate) {
      toast.error('Enter "Amount per hour" before saving.');
      return;
    }
    const keys = [...checkedKeys];
    if (!keys.length) {
      toast.info("Select at least one record to mark as paid.");
      return;
    }
    const records = keys.map((k) => {
      const r = visitRows.find((x) => String(x.visitId) === k);
      const amount = r ? Number(amountOf(r.onSiteMinutes).toFixed(2)) : 0;
      return { recordKey: k, amount };
    });
    setSavingPaid(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/billing-status/mark-paid", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ records, rate }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("Marked as paid.");
        setCheckedKeys(new Set());
        setAmountPerHour(""); // reset rate field after saving
        fetchReport(appliedRange.from, appliedRange.to);
      } else {
        toast.error(data.message || "Failed to save");
      }
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingPaid(false);
    }
  };

  const fetchReport = async (from = "", to = "") => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (from) params.append("from", from);
      if (to) params.append("to", to);
      const qs = params.toString();
      const res = await fetch(getBaseUrl() + "/api/fatigue-visits/billing-report" + (qs ? `?${qs}` : ""), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRows(Array.isArray(data.data?.rows) ? data.data.rows : []);
        setAppliedRange({ from, to });
        setCheckedKeys(new Set());
      } else {
        setRows([]);
        toast.error(data.message || "Failed to load billing report");
      }
    } catch {
      setRows([]);
      toast.error("Failed to load billing report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const rate = amountPerHour === "" || amountPerHour == null ? 0 : Number(amountPerHour);
  const validRate = !Number.isNaN(rate) && rate > 0;
  const hoursOf = (mins) => Number(mins || 0) / 60;
  const amountOf = (mins) => hoursOf(mins) * (validRate ? rate : 0);
  // Paid rows keep their frozen amount; unpaid rows use the current rate.
  const rowAmount = (r) => {
    if ((r.billingStatus || "unpaid") === "paid" && r.paidAmount != null) return Number(r.paidAmount);
    // Days booked to Taylors HQ — time off and manager-granted rest — are the company's
    // own cost. They stay on the report so time off is visible, but never contribute to
    // a customer's total.
    if (r.nonBillable) return null;
    return validRate ? amountOf(r.onSiteMinutes) : null;
  };
  // Amount shown in the table: paid rows always; unpaid rows only when ticked.
  const shownAmount = (r) => {
    if ((r.billingStatus || "unpaid") === "paid") return r.paidAmount != null ? Number(r.paidAmount) : (validRate ? amountOf(r.onSiteMinutes) : null);
    if (r.nonBillable) return null;
    return checkedKeys.has(String(r.visitId)) && validRate ? amountOf(r.onSiteMinutes) : null;
  };

  // Workers summary (group by worker)
  const workerRows = useMemo(() => {
    const byWorker = {};
    for (const r of rows) {
      const k = workerKeyOf(r);
      if (!byWorker[k]) byWorker[k] = { key: k, workerName: r.workerName || "—", visits: 0, minutes: 0 };
      byWorker[k].visits += 1;
      byWorker[k].minutes += Number(r.onSiteMinutes || 0);
    }
    return Object.values(byWorker).sort((a, b) => String(a.workerName).localeCompare(String(b.workerName)));
  }, [rows]);

  // Selected worker's visits — date-wise (per Work Sheets date), sorted by date
  const visitRows = useMemo(() => {
    if (!selected) return [];
    return rows
      .filter((r) => workerKeyOf(r) === selected.key)
      .slice()
      .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  }, [rows, selected]);

  // Group a worker's rows per shift/date (one work sheet record = one row).
  // A date with multiple visits becomes a single row; the visits open in a modal.
  const groupedRows = useMemo(() => {
    const map = new Map();
    for (const r of visitRows) {
      const key = String(r.entryId || r.visitId);
      if (!map.has(key)) map.set(key, { key, entryId: r.entryId, date: r.date, items: [] });
      map.get(key).items.push(r);
    }
    return [...map.values()].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  }, [visitRows]);

  const handleMonthChange = (value) => {
    setMonth(value);
    if (!value) {
      fetchReport();
      return;
    }
    const [y, m] = value.split("-").map(Number);
    const first = toYmd(new Date(y, m - 1, 1));
    const last = toYmd(new Date(y, m, 0)); // last day of month
    fetchReport(first, last);
  };
  const selectPeriod = (type) => {
    setPeriodType(type);
    setMonth("");
    setWeekStart(null);
    if (type === "week") {
      const mon = getMonday(new Date());
      setWeekStart(mon);
      fetchReport(toYmd(mon), toYmd(addDays(mon, 6)));
    } else {
      fetchReport();
    }
  };

  const shiftWeek = (delta) => {
    const mon = addDays(weekStart || getMonday(new Date()), delta * 7);
    setWeekStart(mon);
    fetchReport(toYmd(mon), toYmd(addDays(mon, 6)));
  };

  const handleReset = () => {
    setMonth("");
    setWeekStart(null);
    setPeriodType("month");
    fetchReport();
  };

  const periodText =
    periodType === "week" && weekStart
      ? `Period: ${formatDateLabel(toYmd(weekStart))} – ${formatDateLabel(toYmd(addDays(weekStart, 6)))}`
      : month
      ? `Period: ${new Date(month + "-01").toLocaleDateString("en-GB", { month: "long", year: "numeric" })}`
      : "Period: All time";

  const generatePDF = async () => {
    const isDetail = Boolean(selected);
    if ((isDetail ? visitRows : workerRows).length === 0) {
      toast.error("No data to export.");
      return;
    }
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const primaryColor = [139, 22, 52];
    const headerHeight = 30;
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, headerHeight, "F");

    await new Promise((resolve) => {
      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";
      logoImg.onload = function onLoad() {
        try {
          const logoWidth = 70;
          const logoHeight = logoWidth * (logoImg.height / logoImg.width);
          const canvas = document.createElement("canvas");
          const scale = 2.5;
          canvas.width = logoImg.width * scale;
          canvas.height = logoImg.height * scale;
          const ctx = canvas.getContext("2d");
          ctx.scale(scale, scale);
          ctx.drawImage(logoImg, 0, 0);
          doc.addImage(canvas.toDataURL("image/png", 1.0), "PNG", 10, (headerHeight - logoHeight) / 2, logoWidth, logoHeight, "", "NONE");
        } catch {}
        resolve();
      };
      logoImg.onerror = () => resolve();
      logoImg.src = onsiteproLogo;
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(255, 255, 255);
    doc.text("Billing Report", 90, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    let infoY = 18.5;
    const showPeriod = (periodType === "week" && weekStart) || Boolean(month);
    if (showPeriod) { doc.text(periodText, 90, infoY); infoY += 4.5; }
    const grandAmt = isDetail
      ? visitRows.reduce((s, r) => { const a = rowAmount(r); return s + (a != null ? a : 0); }, 0)
      : workerRows.reduce((s, r) => s + (validRate ? amountOf(r.minutes) : 0), 0);
    const grandAny = isDetail ? visitRows.some((r) => rowAmount(r) != null) : validRate;
    doc.text(`Total Amount: ${grandAny ? `£${grandAmt.toFixed(2)}` : "-"}`, 90, infoY);

    if (isDetail) {
      const totMins = visitRows.reduce((s, r) => s + Number(r.onSiteMinutes || 0), 0);
      const totAmt = visitRows.reduce((s, r) => { const a = rowAmount(r); return s + (a != null ? a : 0); }, 0);
      const totAmtAny = visitRows.some((r) => rowAmount(r) != null);
      autoTable(doc, {
        startY: headerHeight + 12,
        head: [["#", "Date", "Site", "Site On", "Site Off", "Hours", "Amount (£)"]],
        body: visitRows.map((r, i) => {
          const a = rowAmount(r);
          const typeLbl = r.holidayLabel ? r.holidayLabel : (r.noVisit ? "Shift" : "Visit");
          return [
            i + 1,
            formatDateLabel(r.date),
            `${r.siteName || r.siteId || "-"} (${typeLbl})`,
            r.timeOn || "-",
            r.timeOff || "-",
            hoursOf(r.onSiteMinutes).toFixed(2),
            a != null ? a.toFixed(2) : "-",
          ];
        }),
        foot: [["", "", "TOTAL", "", "", hoursOf(totMins).toFixed(2), totAmtAny ? totAmt.toFixed(2) : "-"]],
        styles: { font: "helvetica", fontSize: 9, cellPadding: 3, lineColor: [203, 213, 225], lineWidth: 0.2 },
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" },
        footStyles: { fillColor: [248, 241, 244], textColor: [139, 22, 52], fontStyle: "bold" },
        margin: { left: 10, right: 10 },
      });
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(139, 22, 52);
      doc.text(`Worker: ${selected.workerName}`, 10, headerHeight + 8);
      doc.save(`Billing_${String(selected.workerName).replace(/[^\w]+/g, "_")}.pdf`);
    } else {
      const totVisits = workerRows.reduce((s, r) => s + r.visits, 0);
      const totMins = workerRows.reduce((s, r) => s + r.minutes, 0);
      autoTable(doc, {
        startY: headerHeight + 8,
        head: [["#", "Worker", "Visits", "Total Hours", "Total Amount (£)"]],
        body: workerRows.map((r, i) => [
          i + 1,
          r.workerName,
          r.visits,
          hoursOf(r.minutes).toFixed(2),
          validRate ? amountOf(r.minutes).toFixed(2) : "—",
        ]),
        foot: [["", "TOTAL", totVisits, hoursOf(totMins).toFixed(2), validRate ? amountOf(totMins).toFixed(2) : "—"]],
        styles: { font: "helvetica", fontSize: 9, cellPadding: 3, lineColor: [203, 213, 225], lineWidth: 0.2 },
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" },
        footStyles: { fillColor: [248, 241, 244], textColor: [139, 22, 52], fontStyle: "bold" },
        margin: { left: 10, right: 10 },
      });
      doc.save("Billing_Report.pdf");
    }
    toast.success("Billing report PDF generated.");
  };

  const exportExcel = () => {
    if (!selected || !visitRows.length) {
      toast.error("No data to export.");
      return;
    }
    const headers = ["#", "Date", "Site", "Type", "Site On", "Site Off", "Hours", "Amount (£)", "Status"];
    const body = visitRows.map((r, i) => {
      const type = r.holidayLabel ? r.holidayLabel : (r.noVisit ? "Shift" : "Visit");
      const a = rowAmount(r);
      return [
        i + 1,
        formatDateLabel(r.date),
        r.siteName || r.siteId || "",
        type,
        r.timeOn || "",
        r.timeOff || "",
        Number(hoursOf(r.onSiteMinutes).toFixed(2)),
        a != null ? Number(a.toFixed(2)) : "",
        (r.billingStatus || "unpaid") === "paid" ? "Paid" : "Unpaid",
      ];
    });
    const totalMins = visitRows.reduce((s, r) => s + Number(r.onSiteMinutes || 0), 0);
    const totalAmt = visitRows.reduce((s, r) => { const a = rowAmount(r); return s + (a != null ? a : 0); }, 0);
    const anyAmt = visitRows.some((r) => rowAmount(r) != null);
    const totalRow = ["", "", "", "", "", "TOTAL", Number(hoursOf(totalMins).toFixed(2)), anyAmt ? Number(totalAmt.toFixed(2)) : "", ""];

    const aoa = [[`Billing Report — ${selected.workerName}`], [], headers, ...body, totalRow];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 5 }, { wch: 14 }, { wch: 26 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Billing");
    const safe = String(selected.workerName || "worker").replace(/[^\w]+/g, "_");
    XLSX.writeFile(wb, `Billing_${safe}.xlsx`);
    toast.success("Excel exported.");
  };

  // ----- render helpers -----
  const summaryCols = "60px 2.4fr 1fr";
  const detailCols = "56px 60px 1.4fr 1.8fr 1fr 1fr 1fr 1.2fr 1.2fr";
  const tableMinWidth = selected ? "1000px" : "720px";
  // HQ days are never invoiced, so they are not selectable for "mark paid" either.
  const payableUnpaid = (r) => (r.billingStatus || "unpaid") !== "paid" && r.payable && !r.nonBillable;
  const allUnpaidChecked =
    visitRows.length > 0 &&
    visitRows.filter(payableUnpaid).every((r) => checkedKeys.has(String(r.visitId))) &&
    visitRows.some(payableUnpaid);

  return (
    <div style={{ background: "#ffffff", borderRadius: "20px", boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)", padding: "24px", marginBottom: "24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "14px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {selected ? (
            <button type="button" onClick={() => setSelected(null)} title="Back to workers" style={{ background: "#fff", color: THEME, border: "1px solid #8b1634", borderRadius: "10px", width: "40px", height: "40px", cursor: "pointer", fontSize: "14px" }}>
              <i className="fa fa-arrow-left" />
            </button>
          ) : null}
          <h2 style={{ fontSize: "24px", fontWeight: 700, color: "#1a202c", margin: 0 }}>
            Billing Report{selected ? ` — ${selected.workerName}` : ""}
          </h2>
        </div>
        {selected && (
          <button type="button" onClick={exportExcel} style={{ background: THEME, color: "#fff", border: "none", borderRadius: "10px", padding: "10px 16px", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>
            <i className="fa fa-file-excel-o" style={{ marginRight: 8 }} />
            Export Excel
          </button>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "flex-end", gap: "12px", flexWrap: "wrap", marginBottom: "20px" }}>
        {selected && (
        <>
        <div>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: THEME, marginBottom: "6px" }}>Period</label>
          <div style={{ display: "inline-flex", border: "1px solid #8b1634", borderRadius: "8px", overflow: "hidden" }}>
            {["month", "week"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => selectPeriod(t)}
                style={{ padding: "8px 16px", border: "none", cursor: "pointer", fontWeight: 600, fontSize: "13px", background: periodType === t ? "#8b1634" : "#fff", color: periodType === t ? "#fff" : THEME }}
              >
                {t === "month" ? "Monthly" : "Weekly"}
              </button>
            ))}
          </div>
        </div>
        {periodType === "month" ? (
        <div ref={monthRef} style={{ position: "relative" }}>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: THEME, marginBottom: "6px" }}>Month</label>
          <div
            onClick={() => {
              setPickerYear(month ? Number(month.split("-")[0]) : new Date().getFullYear());
              setMonthOpen((o) => !o);
            }}
            style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #8b1634", background: "#fff", fontSize: "14px", cursor: "pointer", minWidth: "180px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}
          >
            <span style={{ color: month ? THEME : "#94a3b8", fontWeight: month ? 600 : 400 }}>
              {month ? new Date(month + "-01").toLocaleDateString("en-GB", { month: "long", year: "numeric" }) : "Select month"}
            </span>
            <i className="fa fa-calendar" style={{ color: THEME, fontSize: "13px" }} />
          </div>
          {monthOpen ? (
            <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 40, background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px", boxShadow: "0 10px 24px rgba(2,6,23,0.14)", padding: "10px", width: "240px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                <button type="button" onClick={() => setPickerYear((y) => y - 1)} style={{ background: "transparent", border: "none", color: THEME, fontSize: "18px", cursor: "pointer", padding: "0 8px" }}>‹</button>
                <span style={{ fontWeight: 700, color: THEME, fontSize: "15px" }}>{pickerYear}</span>
                <button type="button" onClick={() => setPickerYear((y) => y + 1)} style={{ background: "transparent", border: "none", color: THEME, fontSize: "18px", cursor: "pointer", padding: "0 8px" }}>›</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "6px" }}>
                {MONTHS.map((mName, i) => {
                  const isSel = month && Number(month.split("-")[0]) === pickerYear && Number(month.split("-")[1]) === i + 1;
                  return (
                    <button
                      key={mName}
                      type="button"
                      onClick={() => {
                        setMonthOpen(false);
                        handleMonthChange(`${pickerYear}-${String(i + 1).padStart(2, "0")}`);
                      }}
                      style={{
                        padding: "8px 0",
                        borderRadius: "8px",
                        border: isSel ? "1px solid #8b1634" : "1px solid #f1dce4",
                        background: isSel ? "#8b1634" : "#fff",
                        color: isSel ? "#fff" : THEME,
                        fontWeight: 600,
                        fontSize: "13px",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = "#fdf2f6"; }}
                      onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = "#fff"; }}
                    >
                      {mName}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
        ) : (
        <div>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: THEME, marginBottom: "6px" }}>Week</label>
          <div style={{ display: "inline-flex", alignItems: "center", border: "1px solid #8b1634", borderRadius: "8px", overflow: "hidden", background: "#fff" }}>
            <button type="button" onClick={() => shiftWeek(-1)} title="Previous week" style={{ padding: "8px 12px", border: "none", background: "#fff", color: THEME, cursor: "pointer", fontSize: "16px" }}>‹</button>
            <span style={{ padding: "8px 12px", color: THEME, fontWeight: 600, fontSize: "13px", whiteSpace: "nowrap", minWidth: "190px", textAlign: "center" }}>
              {weekStart ? `${formatDateLabel(toYmd(weekStart))} – ${formatDateLabel(toYmd(addDays(weekStart, 6)))}` : "—"}
            </span>
            <button type="button" onClick={() => shiftWeek(1)} title="Next week" style={{ padding: "8px 12px", border: "none", background: "#fff", color: THEME, cursor: "pointer", fontSize: "16px" }}>›</button>
          </div>
        </div>
        )}
        <button type="button" onClick={handleReset} style={{ background: "#fff", color: THEME, border: "1px solid #8b1634", borderRadius: "8px", padding: "9px 18px", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>Reset</button>
        </>
        )}
        {selected && (
        <div style={{ marginLeft: "auto" }}>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: THEME, marginBottom: "6px" }}>Amount per hour (£)</label>
          <input
            type="number"
            min="0"
            step="any"
            value={amountPerHour}
            onChange={(e) => {
              if (checkedKeys.size === 0) {
                toast.info("Please tick at least one record first, then enter the rate.", { toastId: "billing-tick-warn" });
                return; // don't allow typing the rate until a record is ticked
              }
              const digits = e.target.value.replace(/\D/g, "").slice(0, 3); // max 3-digit number
              setAmountPerHour(digits);
            }}
            maxLength={3}
            placeholder="Enter rate"
            style={{ padding: "8px 10px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", minWidth: "160px", background: "#fff" }}
          />
        </div>
        )}
      </div>

      <div style={{ overflowX: "auto", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
        {selected ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: detailCols, minWidth: tableMinWidth, background: "#e8f4fc", padding: "12px 16px", borderBottom: "1px solid #e2e8f0", gap: "16px", alignItems: "center" }}>
              <div style={headerCell}>
                <input
                  type="checkbox"
                  checked={allUnpaidChecked}
                  onChange={(e) => {
                    const check = e.target.checked;
                    setCheckedKeys(() => {
                      if (!check) return new Set();
                      const s = new Set();
                      visitRows.forEach((r) => { if (payableUnpaid(r)) s.add(String(r.visitId)); });
                      return s;
                    });
                  }}
                  style={{ width: 16, height: 16, accentColor: THEME, cursor: "pointer" }}
                  title="Select all unpaid"
                />
              </div>
              <div style={headerCell}>#</div>
              <div style={headerCell}>Date</div>
              <div style={headerCell}>Site</div>
              <div style={headerCell}>Site On</div>
              <div style={headerCell}>Site Off</div>
              <div style={headerCell}>Hours</div>
              <div style={headerCell}>Amount (£)</div>
              <div style={headerCell}>Status</div>
            </div>
            {loading ? (
              <div style={{ padding: "40px", textAlign: "center", color: THEME, fontWeight: 600 }}><i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />Loading...</div>
            ) : visitRows.length === 0 ? (
              <div style={{ padding: "40px", textAlign: "center", color: "#718096", fontSize: "16px" }}>No visits for this worker.</div>
            ) : (
              <>
                {groupedRows.map((g, i) => {
                  const multi = g.items.length > 1;
                  const first = g.items[0];
                  const totalMin = g.items.reduce((s, it) => s + Number(it.onSiteMinutes || 0), 0);
                  const groupAmount = g.items.reduce((s, it) => { const a = shownAmount(it); return s + (a != null ? a : 0); }, 0);
                  const anyAmount = g.items.some((it) => shownAmount(it) != null);
                  const allPaid = g.items.every((it) => (it.billingStatus || "unpaid") === "paid");
                  const unpaidKeys = g.items.filter(payableUnpaid).map((it) => String(it.visitId));
                  const hasPayableUnpaid = unpaidKeys.length > 0;
                  const checkboxDisabled = allPaid || !hasPayableUnpaid;
                  const groupChecked = allPaid || (hasPayableUnpaid && unpaidKeys.every((k) => checkedKeys.has(k)));
                  return (
                    <div
                      key={g.key || i}
                      style={{ display: "grid", gridTemplateColumns: detailCols, minWidth: tableMinWidth, padding: "16px", borderBottom: "1px solid #f1f5f9", gap: "16px", alignItems: "center" }}
                    >
                      <div onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={groupChecked}
                          disabled={checkboxDisabled}
                          onChange={() => toggleGroup(unpaidKeys)}
                          style={{ width: 16, height: 16, accentColor: THEME, cursor: checkboxDisabled ? "not-allowed" : "pointer" }}
                          title={allPaid ? "Already paid" : !hasPayableUnpaid ? "No billable hours" : "Tick and press Save to mark paid"}
                        />
                      </div>
                      <div style={dataCell}>{i + 1}</div>
                      <div style={dataCell}>{formatDateLabel(g.date)}</div>
                      <div style={{ ...dataCell, display: "flex", alignItems: "center", gap: "8px" }}>
                        {multi ? (
                          <>
                            <span onClick={() => setDetailModal({ date: g.date, items: g.items })} style={{ color: THEME, fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}>{g.items.length} visits</span>
                            <span style={{ flexShrink: 0, fontSize: "10px", fontWeight: 700, color: "#fff", background: THEME, borderRadius: "999px", padding: "1px 8px" }}>Visit</span>
                          </>
                        ) : (
                          <>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }} title={first.siteName || first.siteId || ""}>{first.siteName || first.siteId || "—"}</span>
                            {first.nonBillable ? (
                              <span style={{ flexShrink: 0, fontSize: "10px", fontWeight: 700, color: "#3730a3", background: "#e0e7ff", border: "1px solid #c7d2fe", borderRadius: "999px", padding: "1px 8px" }}>Non-billable</span>
                            ) : null}
                            {first.holidayLabel ? (
                              <span style={{ flexShrink: 0, fontSize: "10px", fontWeight: 700, color: "#92400e", background: "#fef3c7", border: "1px solid #fcd9a3", borderRadius: "999px", padding: "1px 8px" }}>{first.holidayLabel}</span>
                            ) : first.noVisit ? (
                              <span style={{ flexShrink: 0, fontSize: "10px", fontWeight: 700, color: "#64748b", background: "#eef2f7", border: "1px solid #d8dee9", borderRadius: "999px", padding: "1px 8px" }}>Shift</span>
                            ) : (
                              <span style={{ flexShrink: 0, fontSize: "10px", fontWeight: 700, color: "#fff", background: THEME, borderRadius: "999px", padding: "1px 8px" }}>Visit</span>
                            )}
                          </>
                        )}
                      </div>
                      <div style={dataCell}>{multi ? (first.dutyOn || "—") : (first.timeOn || "—")}</div>
                      <div style={dataCell}>{multi ? (first.dutyOff || "—") : (first.timeOff || "—")}</div>
                      <div style={dataCell}>{hoursOf(totalMin).toFixed(2)} h</div>
                      <div style={dataCell}>{anyAmount ? `£${groupAmount.toFixed(2)}` : "—"}</div>
                      <div>
                        {allPaid ? (
                          <span style={{ fontSize: "11px", fontWeight: 700, color: "#fff", background: THEME, borderRadius: "999px", padding: "2px 10px" }}>Paid</span>
                        ) : (
                          <span style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", background: "#eef2f7", border: "1px solid #d8dee9", borderRadius: "999px", padding: "2px 10px" }}>Unpaid</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div style={{ display: "grid", gridTemplateColumns: detailCols, minWidth: tableMinWidth, padding: "14px 16px", gap: "16px", alignItems: "center", background: "#fdf2f6", fontWeight: 700, color: THEME }}>
                  <div />
                  <div />
                  <div />
                  <div>TOTAL</div>
                  <div />
                  <div />
                  <div>{hoursOf(visitRows.reduce((s, r) => s + Number(r.onSiteMinutes || 0), 0)).toFixed(2)} h</div>
                  <div>{(() => {
                    const any = visitRows.some((r) => shownAmount(r) != null);
                    const total = visitRows.reduce((s, r) => { const a = shownAmount(r); return s + (a != null ? a : 0); }, 0);
                    return any ? `£${total.toFixed(2)}` : "—";
                  })()}</div>
                  <div />
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: summaryCols, minWidth: tableMinWidth, background: "#e8f4fc", padding: "12px 16px", borderBottom: "1px solid #e2e8f0", gap: "16px", alignItems: "center" }}>
              <div style={headerCell}>#</div>
              <div style={headerCell}>Worker name</div>
              <div style={headerCell}>Visits</div>
            </div>
            {loading ? (
              <div style={{ padding: "40px", textAlign: "center", color: THEME, fontWeight: 600 }}><i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />Loading...</div>
            ) : workerRows.length === 0 ? (
              <div style={{ padding: "40px", textAlign: "center", color: "#718096", fontSize: "16px" }}>No visits found for the selected range.</div>
            ) : (
              <>
                {workerRows.map((r, i) => (
                  <div
                    key={r.key}
                    onClick={() => setSelected({ key: r.key, workerName: r.workerName })}
                    title="Click to view this worker"
                    style={{ display: "grid", gridTemplateColumns: summaryCols, minWidth: tableMinWidth, padding: "16px", borderBottom: "1px solid #f1f5f9", gap: "16px", alignItems: "center", cursor: "pointer" }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#fafafa"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                  >
                    <div style={dataCell}>{i + 1}</div>
                    <div style={{ ...dataCell, color: THEME, fontWeight: 600 }} title={r.workerName}>{r.workerName}</div>
                    <div style={dataCell}>{r.visits}</div>
                  </div>
                ))}
                <div style={{ display: "grid", gridTemplateColumns: summaryCols, minWidth: tableMinWidth, padding: "14px 16px", gap: "16px", alignItems: "center", background: "#fdf2f6", fontWeight: 700, color: THEME }}>
                  <div />
                  <div>TOTAL</div>
                  <div>{workerRows.reduce((s, r) => s + r.visits, 0)}</div>
                </div>
              </>
            )}
          </>
        )}
      </div>
      {selected && !validRate && rows.length > 0 ? (
        <div style={{ marginTop: "10px", fontSize: "13px", color: "#92400e" }}>Enter "Amount per hour" above to calculate the total amount.</div>
      ) : null}
      {selected && visitRows.length > 0 ? (
        <div style={{ marginTop: "14px", display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={requestSave}
            disabled={savingPaid || checkedKeys.size === 0}
            style={{
              background: savingPaid || checkedKeys.size === 0 ? "#c2a8b0" : THEME,
              color: "#fff", border: "none", borderRadius: "10px", padding: "10px 24px",
              fontWeight: 600, fontSize: "14px",
              cursor: savingPaid || checkedKeys.size === 0 ? "not-allowed" : "pointer",
            }}
          >
            {savingPaid ? "Saving..." : "Save"}
          </button>
        </div>
      ) : null}

      {confirmSave && createPortal(
        <div
          onClick={() => setConfirmSave(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3100, padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: "min(460px, 94vw)", background: "#fff", borderRadius: 14, boxShadow: "0 16px 48px rgba(0,0,0,0.28)", overflow: "hidden" }}
          >
            <div style={{ background: THEME, color: "#fff", padding: "14px 18px", fontWeight: 600, fontSize: 18 }}>
              Confirm payment
            </div>
            <div style={{ padding: "18px", color: "#2d3748", fontSize: 14, lineHeight: 1.5 }}>
              You are about to mark <strong>{checkedKeys.size}</strong> record(s) as <strong>Paid</strong>.
              <div style={{ marginTop: 8, color: "#92400e" }}>
                Once saved, these records are locked as Paid and <strong>cannot be changed again</strong>. Please confirm.
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "0 18px 18px" }}>
              <button
                type="button"
                onClick={() => setConfirmSave(false)}
                disabled={savingPaid}
                style={{ background: "#fff", color: THEME, border: `1px solid ${THEME}`, borderRadius: 10, padding: "9px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePaid}
                disabled={savingPaid}
                style={{ background: THEME, color: "#fff", border: "none", borderRadius: 10, padding: "9px 20px", fontWeight: 600, fontSize: 14, cursor: savingPaid ? "not-allowed" : "pointer" }}
              >
                {savingPaid ? "Saving..." : "Yes, mark Paid"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {detailModal && createPortal(
        <div
          onClick={() => setDetailModal(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000, padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: "min(720px, 94vw)", maxHeight: "85vh", background: "#fff", borderRadius: 14, boxShadow: "0 16px 48px rgba(0,0,0,0.28)", display: "flex", flexDirection: "column", overflow: "hidden" }}
          >
            <div style={{ background: THEME, color: "#fff", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h5 style={{ margin: 0, fontWeight: 600, fontSize: 18 }}>Visits — {formatDateLabel(detailModal.date)}</h5>
              <button type="button" onClick={() => setDetailModal(null)} style={{ background: "transparent", border: "none", color: "#fff", fontSize: 24, lineHeight: 1, cursor: "pointer" }}>×</button>
            </div>
            <div style={{ overflowY: "auto" }}>
              <div style={{ display: "grid", gridTemplateColumns: "50px 2fr 1fr 1fr 1fr 1.2fr 1.1fr", background: "#e8f4fc", padding: "10px 16px", gap: 12, fontWeight: 600, color: THEME, fontSize: 13 }}>
                <div>#</div><div>Site</div><div>Site On</div><div>Site Off</div><div>Hours</div><div>Amount (£)</div><div>Status</div>
              </div>
              {detailModal.items.map((it, idx) => (
                <div key={it.visitId || idx} style={{ display: "grid", gridTemplateColumns: "50px 2fr 1fr 1fr 1fr 1.2fr 1.1fr", padding: "12px 16px", gap: 12, borderTop: "1px solid #f1f5f9", fontSize: 14, alignItems: "center" }}>
                  <div>{idx + 1}</div>
                  <div title={it.siteName || ""}>{it.siteName || it.siteId || "—"}</div>
                  <div>{it.timeOn || "—"}</div>
                  <div>{it.timeOff || "—"}</div>
                  <div>{hoursOf(it.onSiteMinutes).toFixed(2)} h</div>
                  <div>{(() => { const a = rowAmount(it); return a != null ? `£${a.toFixed(2)}` : "—"; })()}</div>
                  <div>
                    {(it.billingStatus || "unpaid") === "paid" ? (
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: THEME, borderRadius: 999, padding: "2px 8px" }}>Paid</span>
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", background: "#eef2f7", border: "1px solid #d8dee9", borderRadius: 999, padding: "2px 8px" }}>Unpaid</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
